import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { inventarioApi, isOfflineError, cache } from '../services/inventarioApi';
import { offlineQueue } from '../services/offlineQueue';
import {
  combinarFechaConHoraActual,
  formatearFechaLocal,
  normalizarFechaISO,
} from '../../../utils/horaLocal';

import { calcularInventario } from '../services/valoracion';
const ENTIDAD = 'movimientos';

export function useMovimientos(metodo = 'PROMEDIO') {
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMovimientos = useCallback(async () => {
    try {
      const data = await inventarioApi.getAll(ENTIDAD);
      setMovimientos(data);
      cache.set(ENTIDAD, data);
    } catch (e) {
      if (isOfflineError(e)) setMovimientos(cache.get(ENTIDAD) || []);
      else console.error('Error fetch movimientos', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMovimientos(); }, [fetchMovimientos]);

  const valoracion = useMemo(() => calcularInventario(movimientos, metodo), [movimientos, metodo]);
  const calcularStock = productoId => valoracion.saldos[String(productoId)]?.cantidad || 0;
  const obtenerPrecioPromedio = productoId => valoracion.saldos[String(productoId)]?.promedio || 0;
  const obtenerPrecioPromedioHasta = (productoId, fechaLimite) => {
    const hasta = calcularInventario(movimientos.filter(m => new Date(m.fecha) <= new Date(fechaLimite)), metodo);
    return hasta.saldos[String(productoId)]?.promedio || 0;
  };
  const calcularCostoMovimiento = m => valoracion.costos[String(m.id || m._id || m.localId)] ?? 0;

  const registrarMovimiento = async (
    productoId, cantidad, precio, tipo, transactionID,
    subTipo = null, metodoPago = 'EFECTIVO', fechaRegistro = null
  ) => {
    const cant = parseFloat(cantidad);
    let pre = parseFloat(precio);
    let precioFijado = null;
    let metodoPagoFinal = metodoPago;

    if (!productoId || isNaN(cant) || cant <= 0) {
      alert("Cantidad no válida.");
      return false;
    }

    if (tipo === 'ENTRADA' && (isNaN(pre) || pre < 0)) {
      alert("El precio es obligatorio para entradas.");
      return false;
    }

    const stock = calcularStock(productoId);

    if (tipo === 'SALIDA' || tipo === 'VENTA') {
      if (cant > stock) {
        alert(`Stock insuficiente. Disponible: ${stock}`);
        return false;
      }
      precioFijado = obtenerPrecioPromedio(productoId);
      if (tipo === 'SALIDA') {
        if (!subTipo) {
          pre = 0;
          metodoPagoFinal = null;
        } else {
          pre = precioFijado;
        }
      } else if (tipo === 'VENTA') {
        if (isNaN(pre) || pre < 0) {
          alert("El precio unitario de venta es obligatorio.");
          return false;
        }
      }
    }

    const body = {
      id: uuid(),
      productoId, cantidad: cant, precio: pre, tipo, subTipo,
      transactionID, metodoPago: metodoPagoFinal,
      precioFijado: precioFijado !== null ? parseFloat(precioFijado.toFixed(5)) : null,
      fecha: combinarFechaConHoraActual(fechaRegistro), anulado: false,
    };

    try {
      const nuevo = await inventarioApi.create(ENTIDAD, body);
      setMovimientos(prev => {
        const updated = [...prev, nuevo];
        cache.set(ENTIDAD, updated);
        return updated;
      });
      return nuevo.id;
    } catch (e) {
      if (isOfflineError(e)) {
        const temp = { id: `temp_${Date.now()}_${uuid()}`, ...body };
        setMovimientos(prev => {
          const updated = [...prev, temp];
          cache.set(ENTIDAD, updated);
          return updated;
        });
        offlineQueue.enqueue({ method: 'POST', entidad: ENTIDAD, body });
        return temp.id;
      }
      console.error('Error registrar movimiento', e);
      return false;
    }
  };

  const editarMovimiento = async (id, cantidad, precio) => {
    const cant = parseFloat(cantidad);
    const pre = parseFloat(precio);

    if (isNaN(cant) || cant <= 0 || isNaN(pre) || pre < 0) {
      alert("Datos inválidos");
      return false;
    }

    try {
      const updated = await inventarioApi.patch(ENTIDAD, { id, cantidad: cant, precio: pre });
      setMovimientos(prev => {
        const list = prev.map(m => m.id === id ? updated : m);
        cache.set(ENTIDAD, list);
        return list;
      });
      await fetchMovimientos();
      return true;
    } catch (e) {
      if (isOfflineError(e)) alert('Conéctate para validar la corrección');
      return false;
    }
  };

  const anularMovimiento = async (id) => {
    if (!window.confirm("¿Anular este registro?")) return false;
    try {
      await inventarioApi.patch(ENTIDAD, { id, anulado: true });
      setMovimientos(prev => {
        const list = prev.map(m => m.id === id ? { ...m, anulado: true } : m);
        cache.set(ENTIDAD, list);
        return list;
      });
      return true;
    } catch (e) {
      if (isOfflineError(e)) alert('Conéctate para validar la corrección');
      return false;
    }
  };

  const obtenerValorInventario = (productoId) =>
    valoracion.saldos[String(productoId)]?.valor || 0;

  const obtenerMovimientosEnEfectivo = () =>
    movimientos.filter(m => m.metodoPago === 'EFECTIVO' && !m.anulado);

  const obtenerMovimientosEnQR = () =>
    movimientos.filter(m => m.metodoPago === 'QR' && !m.anulado);

  const obtenerMovimientosPorFecha = (fecha, incluirAnulados = false) => {
    const formatoFecha = formatearFechaLocal(normalizarFechaISO(fecha));
    return movimientos.filter(m => {
      const fechaMovimiento = formatearFechaLocal(normalizarFechaISO(m.fecha));
      return fechaMovimiento === formatoFecha && (incluirAnulados || !m.anulado);
    });
  };

  const editarFechaMovimiento = async (id, fecha) => {
    try { await inventarioApi.patch(ENTIDAD, { id, fecha }); await fetchMovimientos(); return true; }
    catch (e) {       alert(e.response?.data?.message || 'Conéctate para validar y guardar la corrección');
      return false; }
  };

  return {
    movimientos,
    setMovimientos,
    registrarMovimiento,
    editarMovimiento,
    anularMovimiento,
    calcularStock,
    calcularCostoMovimiento,
    obtenerValorInventario,
    obtenerPrecioPromedio,
    obtenerPrecioPromedioHasta,
    obtenerMovimientosEnEfectivo,
    obtenerMovimientosEnQR,
    obtenerMovimientosPorFecha,
    editarFechaMovimiento,
    loading,
    refetch: fetchMovimientos,
  };
}
